import { MessagesEnum } from '@console/shared/components/messages/enums/messages.enums';
import { MessagesService } from '@console/shared/components/messages/messages.service';
import { RedemptionService } from '../services/redemption.service';

import { Injectable } from '@angular/core';
import {
  ChakaAPIError,
  cleanChakaAPIError,
  PaginatedList,
  ReqSuccessResponse,
} from '../../../../../api/src/public-api';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { IRedemption } from '../interface/IRedemption';
import { RedemptionStatusEnum } from '@console/shared/_enums/RedemptionStatus.enum';

export interface IRedemptionState {
  loading: boolean;
  count?: number;
  redemptions: IRedemption[];
  redemption: IRedemption;
  error?: string;
}

const initialState: IRedemptionState = {
  loading: false,
  redemptions: [],
  redemption: null,
};

@Injectable()
export class RedemptionStateService {
  private subject$ = new BehaviorSubject<IRedemptionState>(initialState);

  state$ = this.subject$.asObservable();

  private messageId: number;

  allowedApprovalStatus: string[] = [
    RedemptionStatusEnum.STARTED,
    RedemptionStatusEnum.PENDING,
    RedemptionStatusEnum.RIA_PENDING,
    RedemptionStatusEnum.On_Hold,
  ].map((item) => item.toUpperCase());

  allowedRejectionStatus: string[] = [
    RedemptionStatusEnum.PENDING,
    RedemptionStatusEnum.On_Hold,
  ].map((item) => item.toUpperCase());

  allowedRequeryStatus: string[] = [
    RedemptionStatusEnum.STARTED,
    RedemptionStatusEnum.PENDING,
    RedemptionStatusEnum.OTHER,
    RedemptionStatusEnum.RIA_PENDING,
    RedemptionStatusEnum.RIA_APPROVED,
    RedemptionStatusEnum.APPROVED,
    RedemptionStatusEnum.On_Hold,
  ].map((item) => item.toUpperCase());

  constructor(
    private redemptionService: RedemptionService,
    private messagesService: MessagesService
  ) {}

  get loading$(): Observable<boolean> {
    return this.subject$.pipe(map((state) => state.loading));
  }

  find(query: object): void {
    this.loading();

    this.showLoadingMessage('Fetching redemptions...');

    this.redemptionService
      .fetchRedemptions(query)
      .pipe(
        catchError((error: ChakaAPIError) => {
          this.handleError(error);
          return throwError(error);
        })
      )
      .subscribe({
        next: this.onFindSuccess.bind(this),
      });
  }

  findById(redemptionId: string): void {
    this.loading();

    this.showLoadingMessage('Fetching redemption...');

    this.redemptionService
      .fetchRedemptionById(redemptionId)
      .pipe(
        catchError((error: ChakaAPIError) => {
          this.handleError(error);
          return throwError(error);
        })
      )
      .subscribe({
        next: this.onRetrieveSuccess.bind(this),
      });
  }

  canBeRequeried(redemptionStatus: string): boolean {
    const currentStatus = redemptionStatus.toUpperCase();

    return this.allowedRequeryStatus.includes(currentStatus);
  }

  requery(redemptionId: string): void {
    this.loading();

    this.showLoadingMessage('Querying redemption...');

    this.redemptionService
      .requeryRedemptionById(redemptionId)
      .pipe(
        take(1),
        catchError((error: ChakaAPIError) => {
          this.handleError(error);
          return throwError(error);
        })
      )
      .subscribe({
        next: this.onRetrieveSuccess.bind(this),
      });
  }

  canBeApproved(redemptionStatus: string): boolean {
    const currentStatus = redemptionStatus.toUpperCase();

    return this.allowedApprovalStatus.includes(currentStatus);
  }

  canBeRejected(redemptionStatus: string): boolean {
    const currentStatus = redemptionStatus.toUpperCase();

    return this.allowedRejectionStatus.includes(currentStatus);
  }

  batchApproveOrReject(
    payload: {
      redemptionIDs: string[];
      status:
        | RedemptionStatusEnum.RIA_APPROVED
        | RedemptionStatusEnum.RIA_REJECTED;
    },
    callback: (success: boolean) => void
  ): void {
    const { redemptionIDs, status } = payload;
    const length = redemptionIDs?.length;

    const loadingMessage = `${
      status === RedemptionStatusEnum.RIA_APPROVED ? 'Approving' : 'Rejecting'
    } ${length} ${length <= 1 ? 'redemption' : 'redemptions'}`;

    this.loading();

    this.showLoadingMessage(loadingMessage);

    this.redemptionService
      .batchApproveOrReject(payload)
      .pipe(
        catchError((error: ChakaAPIError) => {
          this.handleError(error, callback);
          return throwError(error);
        })
      )
      .subscribe({
        next: (response) => {
          // this.requery(redemptionId); for details
          this.messagesService.update({
            type: MessagesEnum.success,
            message: response.message,
          });

          !!callback && callback(true);
        },
      });
  }

  reset(): void {
    this.subject$.next(initialState);
  }

  private onFindSuccess(response: PaginatedList<IRedemption>): void {
    this.subject$.next({
      ...this.subject$.getValue(),
      loading: false,
      redemptions: response?.data,
      count: response?.count,
    });

    if (response?.data?.length > 0) {
      this.messagesService.update({
        id: this.messageId,
        type: MessagesEnum.success,
        message: `Successfully retrieved ${
          response.data.length === 1 ? 'redemption' : 'redemptions'
        }`,
      });
      return;
    }

    this.messagesService.hide(this.messageId);
  }

  private onRetrieveSuccess(data: ReqSuccessResponse<IRedemption>): void {
    this.subject$.next({
      ...this.subject$.getValue(),
      loading: false,
      redemption: data.data,
    });

    this.messagesService.update({
      id: this.messageId,
      type: MessagesEnum.success,
      message: 'Successfully retrieved redemption',
    });
  }

  private handleError(
    res: ChakaAPIError,
    callback?: (success: boolean) => void
  ): void {
    const errorMessage = cleanChakaAPIError(res) || 'An Error occurred';

    this.subject$.next({
      ...this.subject$.getValue(),
      error: errorMessage,
      loading: false,
    });

    this.messagesService.update({
      id: this.messageId,
      type: MessagesEnum.danger,
      message: errorMessage,
    });

    !!callback && callback(false);
  }

  private showLoadingMessage(message: string): void {
    if (this.messageId) {
      this.messagesService.update({
        id: this.messageId,
        type: MessagesEnum.loading,
        message,
        isOpened: true,
      });
      return;
    }

    const { id } = this.messagesService.open(MessagesEnum.loading, message);

    this.messageId = id;
  }

  private loading(): void {
    this.subject$.next({
      ...this.subject$.getValue(),
      loading: true,
    });
  }
}
